import type { PopupElement } from 'inclusive-elements';

import { prefersReducedMotion } from '../utils/animate.ts';
import { isTouchPress } from '../utils/events.ts';
import { suppressNextClick } from './popups.ts';
import { withinPress } from './tapCancelling.ts';

/**
 * Stretch glass controls as a finger drags across them, like Liquid Glass on
 * iOS, springing back when it lifts.
 *
 * A button in a glass capsule stretches the whole capsule. As on iOS, lifting
 * the finger only presses the control if it's still on it, or close. A mouse
 * just presses.
 */
export function enableGlassStretch() {
    document.addEventListener('pointerdown', (event) => {
        if (!event.isPrimary || !isTouchPress(event)) return;

        const target = event.target as Element;
        const el = target.closest<HTMLElement>('.glass');

        // Only glass with `touch-action: none` stretches, so the stylesheet
        // alone decides what doesn't.
        if (!el || target.closest(':disabled') || getComputedStyle(el).touchAction !== 'none') {
            return;
        }

        // A new touch stops any spring back, so it stretches from rest.
        const spring = el.getAnimations().find(({ id }) => id === 'glass-stretch');
        if (spring) {
            spring.cancel();
            el.classList.remove('is-stretching');
        }

        const { pointerId, clientX: startX, clientY: startY } = event;
        const pressed = target.closest<HTMLElement>('.btn, textarea');
        const listening = new AbortController();
        let transform = '';

        // Holding a menu's button opens the menu, which takes over the touch
        // to choose from it (see popups.ts), so the glass lets go.
        const popup = target.closest<PopupElement>('ui-popup');
        const wasOpen = popup?.open;
        const heldOpen = () => popup && !wasOpen && popup.open;

        // The finger must stay near the control to press it, as with other
        // controls. It's measured on the first move, before stretching, so a
        // plain tap skips it.
        let rect: DOMRect;
        const onControl = (event: PointerEvent) => withinPress(rect, startX, startY, event);

        // Shows that lifting the finger now would press the button.
        pressed?.classList.add('is-pressing');

        const move = (event: PointerEvent) => {
            if (event.pointerId !== pointerId) return;
            if (heldOpen()) return release(event);

            if (!transform) {
                rect = el.getBoundingClientRect();
                el.classList.add('is-stretching');
            }

            // The glass leans toward the finger and stretches along the drag,
            // both easing off so a long drag only goes so far.
            //
            // Drags are a share of the screen, so a tall screen stretches as
            // much both ways. The stretch is in pixels, not a ratio, so big
            // buttons don't balloon.
            const dx = (event.clientX - startX) / innerWidth;
            const dy = (event.clientY - startY) / innerHeight;
            const lean = (d: number) => 8 * Math.tanh(d / 0.5);
            const stretchX = 5 * Math.tanh(Math.abs(dx) / 0.6);
            const stretchY = 5 * Math.tanh(Math.abs(dy) / 0.6);

            transform =
                `translate(${lean(dx)}px, ${lean(dy)}px) ` +
                `scale(${1 + (stretchX - stretchY / 2) / rect.width}, ` +
                `${1 + (stretchY - stretchX / 2) / rect.height})`;
            el.style.transform = transform;
            pressed?.classList.toggle('is-pressing', onControl(event));
        };

        const release = (event: PointerEvent) => {
            if (event.pointerId !== pointerId) return;

            listening.abort();

            // Read before the changes below, to avoid recalculating styles.
            const easing = transform && getComputedStyle(el).getPropertyValue('--ease-spring');

            pressed?.classList.remove('is-pressing');

            // The glass can leave the page mid-drag, such as when a loading
            // state replaces it. It then has nothing to press, and no computed
            // easing to spring back with.
            if (!transform || !el.isConnected) return;

            // iOS doesn't count a moved touch as a tap, so send the click
            // ourselves and swallow any iOS sends. It's cancelable, so links'
            // handlers can stop a full page load.
            const click = new MouseEvent('click', { bubbles: true, cancelable: true });
            if (event.type === 'pointerup' && onControl(event) && !heldOpen()) {
                if (pressed instanceof HTMLTextAreaElement) pressed.focus();
                else target.dispatchEvent(click);
            }
            suppressNextClick();

            el.style.transform = '';
            el.animate([{ transform }, { transform: 'none' }], {
                id: 'glass-stretch',
                duration: prefersReducedMotion() ? 0 : 600,
                easing,
            }).onfinish = () => el.classList.remove('is-stretching');
        };

        const { signal } = listening;
        addEventListener('pointermove', move, { passive: true, signal });
        addEventListener('pointerup', release, { signal });
        addEventListener('pointercancel', release, { signal });
    });
}
